/**
 * ✒️ RINTINTIN DESIGN SYSTEM - TYPOGRAPHY TOKENS
 * ==============================================
 * 
 * Système typographique cohérent et accessible
 * Basé sur les standards modernes et l'existant
 */

// ==========================================
// FAMILLES DE POLICES
// ==========================================
export const fontFamilies = {
  // Police principale Rintintin = Lexend Deca (chargée dans public/index.html).
  // Fallback sur la pile système pour rester safe si la webfont met du temps à charger.
  body: [
    '"Lexend Deca"',
    '-apple-system',
    'BlinkMacSystemFont',
    'Segoe UI',
    'Roboto',
    'Oxygen',
    'Ubuntu',
    'Cantarell',
    'sans-serif'
  ].join(', '),

  // Titres : même police pour cohérence (le poids différencie la hiérarchie)
  heading: [
    '"Lexend Deca"',
    '-apple-system',
    'BlinkMacSystemFont',
    'Segoe UI',
    'Roboto',
    'Oxygen',
    'Ubuntu',
    'Cantarell',
    'sans-serif'
  ].join(', '),

  // Police monospace pour le code (inchangée)
  mono: [
    'SFMono-Regular',
    'Menlo',
    'Monaco',
    'Consolas',
    'Liberation Mono',
    'Courier New',
    'monospace'
  ].join(', '),
} as const;

// ==========================================
// TAILLES DE POLICE - ÉCHELLE MODULAIRE
// ==========================================
export const fontSizes = {
  // Textes micro/auxiliaires
  xs: '0.75rem',    // 12px
  sm: '0.875rem',   // 14px
  
  // Texte de base
  base: '1rem',     // 16px (référence)
  md: '1rem',       // 16px (alias)
  
  // Textes moyens
  lg: '1.125rem',   // 18px
  xl: '1.25rem',    // 20px
  
  // Titres
  '2xl': '1.5rem',  // 24px
  '3xl': '1.875rem', // 30px
  '4xl': '2.25rem',  // 36px
  '5xl': '3rem',     // 48px
  '6xl': '3.75rem',  // 60px
  '7xl': '4.5rem',   // 72px
} as const;

// ==========================================
// POIDS DES POLICES
// ==========================================
export const fontWeights = {
  thin: 100,
  extraLight: 200,
  light: 300,
  normal: 400,
  medium: 500,
  semiBold: 600,
  bold: 700,
  extraBold: 800,
  black: 900,
} as const;

// ==========================================
// HAUTEURS DE LIGNE
// ==========================================
export const lineHeights = {
  none: 1,
  tight: 1.25,
  snug: 1.375,
  normal: 1.5,
  relaxed: 1.625,
  loose: 2,
  
  // Aliases sémantiques
  heading: 1.25,
  body: 1.5,
  caption: 1.375,
} as const;

// ==========================================
// ESPACEMENT DES LETTRES
// ==========================================
export const letterSpacings = {
  tighter: '-0.05em',
  tight: '-0.025em',
  normal: '0',
  wide: '0.025em',
  wider: '0.05em',
  widest: '0.1em',
} as const;

// ==========================================
// STYLES TYPOGRAPHIQUES PRÉDÉFINIS
// ==========================================
export const textStyles = {
  // ==========================================
  // TITRES (HEADINGS)
  // ==========================================
  h1: {
    fontSize: fontSizes['4xl'],
    fontWeight: fontWeights.bold,
    lineHeight: lineHeights.tight,
    letterSpacing: letterSpacings.tight,
    fontFamily: fontFamilies.heading,
  },

  h2: {
    fontSize: fontSizes['3xl'],
    fontWeight: fontWeights.semiBold,
    lineHeight: lineHeights.tight,
    letterSpacing: letterSpacings.tight,
    fontFamily: fontFamilies.heading,
  },

  h3: {
    fontSize: fontSizes['2xl'],
    fontWeight: fontWeights.semiBold,
    lineHeight: lineHeights.snug,
    letterSpacing: letterSpacings.normal,
    fontFamily: fontFamilies.heading,
  },

  h4: {
    fontSize: fontSizes.xl,
    fontWeight: fontWeights.medium,
    lineHeight: lineHeights.snug,
    letterSpacing: letterSpacings.normal,
    fontFamily: fontFamilies.heading,
  },

  h5: {
    fontSize: fontSizes.lg,
    fontWeight: fontWeights.medium,
    lineHeight: lineHeights.normal,
    letterSpacing: letterSpacings.normal,
    fontFamily: fontFamilies.heading,
  },

  h6: {
    fontSize: fontSizes.base,
    fontWeight: fontWeights.medium,
    lineHeight: lineHeights.normal,
    letterSpacing: letterSpacings.normal,
    fontFamily: fontFamilies.heading,
  },

  // ==========================================
  // CORPS DE TEXTE (BODY)
  // ==========================================
  bodyLarge: {
    fontSize: fontSizes.lg,
    fontWeight: fontWeights.normal,
    lineHeight: lineHeights.relaxed,
    letterSpacing: letterSpacings.normal,
    fontFamily: fontFamilies.body,
  },

  body: {
    fontSize: fontSizes.base,
    fontWeight: fontWeights.normal,
    lineHeight: lineHeights.normal,
    letterSpacing: letterSpacings.normal,
    fontFamily: fontFamilies.body,
  },

  bodySmall: {
    fontSize: fontSizes.sm,
    fontWeight: fontWeights.normal,
    lineHeight: lineHeights.normal,
    letterSpacing: letterSpacings.normal,
    fontFamily: fontFamilies.body,
  },

  // ==========================================
  // TEXTES SPÉCIALISÉS
  // ==========================================
  caption: {
    fontSize: fontSizes.xs,
    fontWeight: fontWeights.normal,
    lineHeight: lineHeights.caption,
    letterSpacing: letterSpacings.wide,
    fontFamily: fontFamilies.body,
  },

  overline: {
    fontSize: fontSizes.xs,
    fontWeight: fontWeights.medium,
    lineHeight: lineHeights.none,
    letterSpacing: letterSpacings.widest,
    textTransform: 'uppercase' as const,
    fontFamily: fontFamilies.body,
  },

  button: {
    fontSize: fontSizes.sm,
    fontWeight: fontWeights.medium,
    lineHeight: lineHeights.none,
    letterSpacing: letterSpacings.wide,
    fontFamily: fontFamilies.body,
  },

  buttonLarge: {
    fontSize: fontSizes.base,
    fontWeight: fontWeights.medium,
    lineHeight: lineHeights.none,
    letterSpacing: letterSpacings.normal,
    fontFamily: fontFamilies.body,
  },

  // ==========================================
  // TEXTES DE FORMULAIRES
  // ==========================================
  label: {
    fontSize: fontSizes.sm,
    fontWeight: fontWeights.medium,
    lineHeight: lineHeights.normal,
    letterSpacing: letterSpacings.normal,
    fontFamily: fontFamilies.body,
  },

  input: {
    fontSize: fontSizes.base,
    fontWeight: fontWeights.normal,
    lineHeight: lineHeights.normal,
    letterSpacing: letterSpacings.normal,
    fontFamily: fontFamilies.body,
  },

  helperText: {
    fontSize: fontSizes.xs,
    fontWeight: fontWeights.normal,
    lineHeight: lineHeights.caption,
    letterSpacing: letterSpacings.normal,
    fontFamily: fontFamilies.body,
  },

  // ==========================================
  // CODE ET DONNÉES
  // ==========================================
  code: {
    fontSize: fontSizes.sm,
    fontWeight: fontWeights.normal,
    lineHeight: lineHeights.normal,
    letterSpacing: letterSpacings.normal,
    fontFamily: fontFamilies.mono,
  },

  codeBlock: {
    fontSize: fontSizes.sm,
    fontWeight: fontWeights.normal,
    lineHeight: lineHeights.relaxed,
    letterSpacing: letterSpacings.normal,
    fontFamily: fontFamilies.mono,
  },
} as const;

// ==========================================
// TYPES POUR TYPESCRIPT
// ==========================================
export type FontSize = keyof typeof fontSizes;
export type FontWeight = keyof typeof fontWeights;
export type LineHeight = keyof typeof lineHeights;
export type LetterSpacing = keyof typeof letterSpacings;
export type TextStyle = keyof typeof textStyles;

// ==========================================
// HELPERS POUR UTILISATION
// ==========================================
export const getTextStyle = (style: TextStyle) => {
  return textStyles[style];
};

// ==========================================
// RESPONSIVE TYPOGRAPHY (optionnel)
// ==========================================
export const responsiveTextStyles = {
  heroTitle: {
    fontSize: {
      base: fontSizes['3xl'],
      md: fontSizes['5xl'],
      lg: fontSizes['6xl'],
    },
    fontWeight: fontWeights.bold,
    lineHeight: lineHeights.tight,
    letterSpacing: letterSpacings.tight,
  },

  pageTitle: {
    fontSize: {
      base: fontSizes['2xl'],
      md: fontSizes['3xl'],
      lg: fontSizes['4xl'],
    },
    fontWeight: fontWeights.semiBold,
    lineHeight: lineHeights.tight,
  },
} as const;
